import React, { useEffect, useMemo, useState } from "react";
import Layout from "src/components/layout/dashboard/Layout";
import PageHeader from "src/components/common/PageHeader";
import Card from "src/components/common/card/Card";
import CardHeader from "src/components/common/card/CardHeader";
import Button from "src/components/ui/Button";
import ContactSupportModal from "src/components/modals/ContactSupportModal";
import { useAppSelector } from "src/redux/hooks";
import authTenantApis from "src/api/authTenant";
import { PlanUsageResponse } from "src/types/planUsage.types";
import { formatDateTime } from "src/utils/date-time";

const PlanUsagePage: React.FC = () => {
  const {
    user: { data: user },
  } = useAppSelector((state) => state.authTenant);
  const isTenantAdmin =
    user?.role === "admin" ||
    user?.roleDetails?.name?.toLowerCase() === "admin";

  const [planUsage, setPlanUsage] = useState<PlanUsageResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);

  useEffect(() => {
    if (!isTenantAdmin) return;
    const loadPlanUsage = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await authTenantApis.getPlanUsage();
        setPlanUsage(response as PlanUsageResponse);
      } catch (err: any) {
        setError(
          err?.message || "Failed to load plan usage details."
        );
      } finally {
        setLoading(false);
      }
    };

    loadPlanUsage();
  }, [isTenantAdmin]);

  const usageItems = useMemo(() => {
    const usage = planUsage?.usage;
    const limits = planUsage?.plan?.features;
    if (!usage || !limits) return [];

    return [
      { key: "totalUsers", label: "Users" },
      { key: "totalProducts", label: "Products" },
      { key: "totalTransactions", label: "Transactions" },
      { key: "totalSaleOrders", label: "Sale Orders" },
      { key: "totalPurchaseOrders", label: "Purchase Orders" },
      { key: "totalSuppliers", label: "Suppliers" },
    ].map((item) => {
      const used = usage[item.key as keyof typeof usage] ?? 0;
      const limit = limits[item.key as keyof typeof limits] ?? 0;
      const percent =
        limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
      return { ...item, used, limit, percent };
    });
  }, [planUsage]);

  return (
    <Layout
      meta={{
        title: "Plan Usage | Invyntra",
        description: "Track plan usage and limits for your workspace.",
        keywords: "plan, usage, subscription, invyntra",
      }}
    >
      <div className="space-y-4">
        <PageHeader
          title="Plan Usage"
          breadcrumbData={[
            { label: "Home", path: "/app/dashboard" },
            { label: "Plan Usage", path: "/app/plan-usage" },
          ]}
        />

        {!isTenantAdmin && (
          <Card>
            <div className="p-6 text-center text-sm text-gray-600">
              Only tenant admins can view plan usage details.
            </div>
          </Card>
        )}

        {isTenantAdmin && (
          <>
            <Card>
              <CardHeader
                title="Current Plan"
                description="Plan details and expiry timeline."
                actions={
                  <Button onClick={() => setIsSupportModalOpen(true)}>
                    Extend/Renew Plan
                  </Button>
                }
              />
              <div className="p-6">
                {loading && (
                  <div className="text-sm text-gray-500">
                    Loading plan details...
                  </div>
                )}
                {error && (
                  <div className="text-sm text-red-600">{error}</div>
                )}
                {!loading && !error && (
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    <div>
                      <div className="text-xs uppercase text-gray-400">
                        Plan Name
                      </div>
                      <div className="text-base font-semibold text-gray-900">
                        {planUsage?.plan?.planName || "N/A"}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs uppercase text-gray-400">
                        Plan Code
                      </div>
                      <div className="text-base font-semibold text-gray-900">
                        {planUsage?.plan?.planCode || "N/A"}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs uppercase text-gray-400">
                        Status
                      </div>
                      <div
                        className={`text-base font-semibold ${
                          planUsage?.planStatus === "active"
                            ? "text-green-600"
                            : "text-red-600"
                        }`}
                      >
                        {planUsage?.planStatus || "N/A"}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs uppercase text-gray-400">
                        Starts At
                      </div>
                      <div className="text-base font-semibold text-gray-900">
                        {formatDateTime(
                          planUsage?.plan?.planStartsAt || "",
                          "DD/MM/YYYY hh:mm:ss A"
                        ) || "N/A"}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs uppercase text-gray-400">
                        Ends At
                      </div>
                      <div className="text-base font-semibold text-gray-900">
                        {formatDateTime(
                          planUsage?.plan?.planEndsAt || "",
                          "DD/MM/YYYY hh:mm:ss A"
                        ) || "N/A"}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs uppercase text-gray-400">
                        Duration
                      </div>
                      <div className="text-base font-semibold text-gray-900">
                        {planUsage?.plan?.durationInDays
                          ? `${planUsage.plan.durationInDays} days`
                          : "N/A"}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </Card>

            <Card>
              <CardHeader
                title="Usage Statistics"
                description="Usage against plan limits."
              />
              <div className="p-6 space-y-4">
                {loading && (
                  <div className="text-sm text-gray-500">
                    Loading usage statistics...
                  </div>
                )}
                {error && (
                  <div className="text-sm text-red-600">{error}</div>
                )}
                {!loading && !error && usageItems.length === 0 && (
                  <div className="text-sm text-gray-500">
                    Plan usage details are not available.
                  </div>
                )}
                {!loading &&
                  !error &&
                  usageItems.map((item) => (
                    <div key={item.key} className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-medium text-gray-700">
                          {item.label}
                        </span>
                        <span className="text-gray-500">
                          {item.used} / {item.limit}
                        </span>
                      </div>
                      <div className="h-2 w-full rounded-full bg-gray-100">
                        <div
                          className="h-2 rounded-full bg-primary-500"
                          style={{ width: `${item.percent}%` }}
                        />
                      </div>
                    </div>
                  ))}
              </div>
            </Card>
          </>
        )}
      </div>

      <ContactSupportModal
        open={isSupportModalOpen}
        onClose={() => setIsSupportModalOpen(false)}
      />
    </Layout>
  );
};

export default PlanUsagePage;
